import { Icon } from '~/ui/components/icon';

const variants = ['pristine', 'reverential', 'empowering', 'dynamic'] as const;

type BaseLogoProps = Omit<React.SVGProps<SVGSVGElement>, 'children'>;

interface VariantLogoProps extends BaseLogoProps {
  variant: (typeof variants)[number];
}

interface RandomLogoProps extends BaseLogoProps {
  randomSeed: string;
}

export type LogoProps = VariantLogoProps | RandomLogoProps;

/** Renders a specified `variant` of the Mellon M or a random variant fixed using a `seed` */
export function Logo(props: LogoProps) {
  let resolvedProps: VariantLogoProps;
  if ('randomSeed' in props) {
    const { randomSeed, ...rest } = props;
    const index = cyrb53(randomSeed) % variants.length;
    resolvedProps = {
      variant: variants[index],
      ...rest,
    };
  } else {
    resolvedProps = props;
  }

  const { variant, ...rest } = resolvedProps;
  // <Icon /> provides built-in lazy-loading so we don't have to handle that here
  return <Icon {...rest} name={`logo-${variant}`} />;
}

/** Fast 53-bit hash */
function cyrb53(str: string): number {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0, ch; i < str.length; i++) {
    ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  // Combine to 53 bits
  return (h2 & 0x1fffff) * 0x100000000 + (h1 >>> 0);
}
